import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShoppingCart, Zap, Repeat, Calendar, Truck, CheckCircle2, XCircle, Filter, Phone, ChevronUp, ChevronDown, Users, List as ListIcon, ShieldCheck, ClipboardCheck, Check, X, UserCheck, UserPlus, UserCog, Wallet, Banknote, CreditCard, MessageSquare, Pencil, Eye, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR, formatDate } from "../../lib/utils.fmt";
import { splitPhone } from "../../lib/phone";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import CustomerUnit from "../../components/CustomerUnit";
import Pagination from "../../components/Pagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { todayIST, shiftIST } from "../../lib/istDate";
import { filterableRoutes, routeOptionLabel } from "../../lib/routeOptions";
import { ALL_SLOTS, slotLabel, slotOptions, slotTone } from "../../lib/orderSlots";

/** The order's saved delivery slot, as a badge that reads at a glance. */
function SlotBadge({ slot, oid }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap ${slotTone(slot)}`}
          data-testid={`order-slot-${oid}`}>
      {slotLabel(slot)}
    </span>
  );
}

const STATUSES = {
  placed: { color: "bg-brand-sand/15 text-brand-sand", Icon: ClipboardCheck, label: "Placed" },
  confirmed: { color: "bg-brand-sky/10 text-brand-sky", Icon: ShieldCheck, label: "Confirmed" },
  pending: { color: "bg-neutral-200 text-neutral-700", Icon: Calendar, label: "Pending" },
  out_for_delivery: { color: "bg-brand-sky/10 text-brand-sky", Icon: Truck, label: "Out for delivery" },
  delivered: { color: "bg-brand-leaf/10 text-brand-leaf", Icon: CheckCircle2, label: "Delivered" },
  failed: { color: "bg-brand-clay/10 text-brand-clay", Icon: XCircle, label: "Failed" },
  skipped: { color: "bg-neutral-100 text-neutral-500", Icon: XCircle, label: "Skipped" },
  cancelled: { color: "bg-brand-clay/10 text-brand-clay", Icon: XCircle, label: "Cancelled" },
};

// Label for the notes field, keyed by the status that produced it — a
// cancelled/failed/skipped delivery's `notes` IS the reason the rider (or
// admin, for an admin-initiated cancel) gave; naming it per-status here
// reads far clearer in the details view than a generic "Notes" label.
const REASON_LABELS = {
  cancelled: "Cancellation reason",
  failed: "Failure reason",
  skipped: "Skip reason",
};

const TYPE_FILTERS = [
  { v: "", label: "All", Icon: Filter },
  { v: "one_time", label: "One-time", Icon: Zap },
  { v: "subscription", label: "Subscriptions", Icon: Repeat },
];

// IST calendar dates (lib/istDate) — "Today" must mean the same day the
// backend, rider app and reports mean, even between 00:00 and 05:30 IST.
function todayISO() { return todayIST(); }
function shiftISO(days) { return shiftIST(days); }

const DATE_PRESETS = [
  { v: "today", label: "Today" },
  { v: "tomorrow", label: "Tomorrow" },
  { v: "yesterday", label: "Yesterday" },
  { v: "last7", label: "Last 7 days" },
  { v: "custom", label: "Custom" },
];

function presetToRange(p, customFrom, customTo) {
  switch (p) {
    case "today": return { from: todayISO(), to: todayISO() };
    case "tomorrow": return { from: shiftISO(1), to: shiftISO(1) };
    case "yesterday": return { from: shiftISO(-1), to: shiftISO(-1) };
    case "last7": return { from: shiftISO(-7), to: todayISO() };
    case "custom": return { from: customFrom, to: customTo };
    default: return { from: shiftISO(-30), to: shiftISO(30) };
  }
}

export default function AdminOrders() {
  const { tenant } = useAuth();
  // The store asks for a flat / house number on every address: flag the
  // customers who don't have one yet (Settings → Require flat number).
  const requireFlat = tenant?.require_flat_number === true;
  const orderEditEnabled = tenant?.order_edit_enabled !== false;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [datePreset, setDatePreset] = useState("today");
  const [customFrom, setCustomFrom] = useState(todayISO());
  const [customTo, setCustomTo] = useState(todayISO());
  const [groupByCustomer, setGroupByCustomer] = useState(false);
  const [sortBy, setSortBy] = useState({ key: "delivery_date", dir: "desc" });
  const [routes, setRoutes] = useState([]);
  const [routeFilter, setRouteFilter] = useState("__all__");
  const [cityFilter, setCityFilter] = useState("__all__");
  const [cities, setCities] = useState([]);
  // Serviceable apartments — the filter only renders when this tenant has
  // some, so a store not using the feature sees no change to this toolbar.
  const [apartments, setApartments] = useState([]);
  const [apartmentFilter, setApartmentFilter] = useState("__all__");
  // Delivery slot as saved on the order (server-side filter); the options are
  // the store's own delivery windows, with a count behind each.
  const [slotFilter, setSlotFilter] = useState(ALL_SLOTS);
  const [slotCounts, setSlotCounts] = useState({});
  const [riders, setRiders] = useState([]);
  const [assignBusy, setAssignBusy] = useState("");   // holds order id while patching

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({ total: 0, one_time: 0, subscription: 0, revenue: 0 });
  const [placedCount, setPlacedCount] = useState(0);
  // Recurring deliveries stranded at `placed` — materialized while no rider
  // was mapped to their (route, slot). Invisible to the rider and locked for
  // the customer until the route is re-staffed, and no other filter finds
  // them ("Awaiting confirmation" is one-time-only).
  const [stuckCount, setStuckCount] = useState(0);
  const [stuckOnly, setStuckOnly] = useState(false);

  // Order edit dialog — product/qty/date/slot/address/instructions on an
  // already-placed one-time order. Only placed/confirmed orders are
  // editable (enforced backend-side too); product/qty are additionally
  // blocked once the order was paid online (no partial-refund mechanism).
  const [editOrder, setEditOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editProducts, setEditProducts] = useState([]);
  const [editAddresses, setEditAddresses] = useState([]);
  const [editAddressError, setEditAddressError] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Order details dialog — read-only, available for EVERY order regardless
  // of status. The list rows only show the cancel/fail/skip reason as a
  // hover-only icon (RemarksBadge) which is easy to miss; this surfaces it
  // as plainly-labeled, always-visible text alongside the rest of the
  // order's info.
  const [viewOrder, setViewOrder] = useState(null);
  // Per-day note on a subscription delivery (order details dialog). A note
  // typed here lands on THIS day's row only — the subscription's own
  // `instructions` is the standing note that shows on every generated day.
  const [dayNote, setDayNote] = useState("");
  const [dayNoteBusy, setDayNoteBusy] = useState(false);
  const openDetails = (o) => {
    setViewOrder(o);
    setDayNote(o?.instructions_source === "day" ? (o.instructions || "") : "");
  };
  const saveDayNote = async () => {
    if (!viewOrder) return;
    setDayNoteBusy(true);
    try {
      const { data } = await api.patch(`/api/admin/orders/${viewOrder.id}`, { instructions: dayNote });
      toast.success(dayNote.trim() ? "Note saved for this delivery" : "Note removed");
      setViewOrder(data.delivery || { ...viewOrder, instructions: dayNote, instructions_source: "day" });
      load(page);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the note"); }
    finally { setDayNoteBusy(false); }
  };

  useEffect(() => {
    api.get("/api/admin/routes").then(({ data }) => setRoutes(data.routes || [])).catch((e) => toast.error(friendlyMessage(e)));
  }, []);
  useEffect(() => {
    api.get("/api/admin/delivery-boys").then(({ data }) => setRiders(data.delivery_boys || [])).catch((e) => toast.error(friendlyMessage(e)));
  }, []);
  useEffect(() => {
    // Active-only: filtering by a disabled apartment is still useful for
    // history, but the common case is picking today's live blocks, and the
    // dropdown stays short. Failure is silent — an unavailable apartment
    // list must not break the orders page.
    api.get("/api/admin/apartments?page_size=100&include_inactive=false")
      .then(({ data }) => setApartments(data.apartments || []))
      .catch(() => setApartments([]));
  }, []);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Only the newest list request may fill the table: an older one answering
  // late (e.g. Morning, clicked just before Evening) is dropped instead of
  // showing its orders under the newer filter.
  const loadSeq = useRef(0);
  const load = async (targetPage) => {
    const seq = ++loadSeq.current;
    setLoadError(null);
    try {
      const { from, to } = presetToRange(datePreset, customFrom, customTo);
      const params = new URLSearchParams();
      if (type) params.set("order_type", type);
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (status) params.set("status", status);
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (routeFilter !== "__all__") params.set("route_id", routeFilter);
      if (cityFilter !== "__all__") params.set("city", cityFilter);
      if (stuckOnly) params.set("stuck", "true");
      if (apartmentFilter !== "__all__") params.set("apartment_id", apartmentFilter);
      if (slotFilter !== ALL_SLOTS) params.set("slot", slotFilter);
      params.set("sort_by", sortBy.key);
      params.set("sort_dir", sortBy.dir);
      params.set("page", String(targetPage ?? page));
      params.set("page_size", String(PAGE_SIZE));
      const { data } = await api.get(`/api/admin/orders?${params}`);
      if (seq !== loadSeq.current) return;
      setOrders(data.orders);
      setTotal(data.total);
      setTotalPages(data.total_pages);
      setCities(data.cities || []);
      setSlotCounts(data.slot_counts || {});
      setCounts(data.counts || { total: 0, one_time: 0, subscription: 0, revenue: 0 });
      setPlacedCount(data.placed_count || 0);
      setStuckCount(data.stuck_count || 0);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };

  // Any filter/search/sort/date change re-queries from page 1. The very
  // first run (mount) shows the full-page loader; every later change just
  // dims the existing table so filtering doesn't flash an empty screen.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      setLoading(true);
      load(1).finally(() => setLoading(false));
      return;
    }
    setPage(1);
    setPageLoading(true);
    load(1).finally(() => setPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, datePreset, customFrom, customTo, status, debouncedSearch, routeFilter, cityFilter, stuckOnly, apartmentFilter, slotFilter, sortBy]);

  const goToPage = (p) => {
    setPage(p);
    setPageLoading(true);
    load(p).finally(() => setPageLoading(false));
  };

  const confirmOrder = async (id) => {
    try {
      await api.post(`/api/admin/orders/${id}/confirm`);
      toast.success("Order confirmed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed"); }
  };

  const cancelOrder = async (id) => {
    const reason = window.prompt("Cancellation reason (optional)") ?? "";
    if (reason === null) return;
    try {
      const { data } = await api.post(`/api/admin/orders/${id}/cancel`, { reason });
      if (data.refund_status === "failed") {
        toast.error(`Order cancelled, but the refund failed at the payment gateway${data.refund_error ? ` (${data.refund_error})` : ""}. Use "Retry refund".`);
      } else if (data.refunded > 0) {
        toast.success(`Order cancelled · ₹${data.refunded} refunded ${data.refund_method === "razorpay" ? "to the original payment method" : "to wallet"}`);
      } else {
        toast.success("Order cancelled");
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  // A cancelled prepaid order whose money has not gone back yet — a gateway
  // failure at cancel time, or a row cancelled before refunds covered it.
  const refundDue = (o) => o.status === "cancelled" && o.payment_method === "online"
    && ["paid", "refund_failed", "refunding"].includes(o.payment_status);
  const retryRefund = async (id) => {
    try {
      const { data } = await api.post(`/api/admin/orders/${id}/refund`);
      toast.success(`₹${data.refunded} refunded ${data.method === "razorpay" ? "to the original payment method" : "to wallet"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const assignRider = async (id, riderId) => {
    setAssignBusy(id);
    try {
      const { data } = await api.patch(`/api/admin/orders/${id}/rider`, {
        rider_id: riderId || null,
      });
      const name = riders.find((r) => r.id === data.delivery_boy_id)?.name;
      if (data.delivery_boy_id) {
        toast.success(`Rider ${name ? "'" + name + "' " : ""}assigned · order ${data.status}`);
      } else {
        toast.success(`Rider cleared · order ${data.status}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rider assignment failed");
    } finally {
      setAssignBusy("");
    }
  };

  const isPaidOnline = editOrder?.payment_method === "online" && editOrder?.payment_status === "paid";

  const openEdit = async (o) => {
    setEditOrder(o);
    setEditForm({
      product_id: o.product_id || "",
      variant_id: o.variant_id || "",
      quantity: o.quantity,
      delivery_date: o.delivery_date,
      slot: o.slot,
      address_id: o.address_id || "",
      instructions: o.instructions || "",
    });
    setEditAddressError("");
    setEditLoading(true);
    // Fetched independently (not Promise.all) so one failing doesn't blank
    // out the other — e.g. an order whose customer record no longer
    // resolves (deleted/orphaned) still lets the admin edit product/qty/
    // date/slot/instructions; only the address picker degrades.
    const productsPromise = api.get("/api/admin/products")
      .then(({ data }) => setEditProducts(data.products || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load products"));
    const addressesPromise = api.get(`/api/admin/customers/${o.customer_id}/addresses`)
      .then(({ data }) => setEditAddresses(data.addresses || []))
      .catch((e) => {
        setEditAddresses([]);
        setEditAddressError(e?.response?.data?.detail || "Failed to load addresses");
      });
    await Promise.all([productsPromise, addressesPromise]);
    setEditLoading(false);
  };

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      // Only send fields the admin actually changed — matches the
      // backend's PATCH semantics (it only re-validates/applies keys
      // present in the body) and avoids resending an untouched address_id
      // that would force a customer-address lookup on every save even
      // when the edit had nothing to do with the address. That lookup can
      // legitimately fail (e.g. an orphaned/deleted customer record),
      // which would otherwise block completely unrelated edits like
      // updating instructions.
      const orig = editOrder;
      const payload = {};
      if (editForm.product_id !== (orig.product_id || "")) payload.product_id = editForm.product_id;
      if (editForm.variant_id !== (orig.variant_id || "")) payload.variant_id = editForm.variant_id;
      if (Number(editForm.quantity) !== orig.quantity) payload.quantity = Number(editForm.quantity);
      if (editForm.delivery_date !== orig.delivery_date) payload.delivery_date = editForm.delivery_date;
      if (editForm.slot !== orig.slot) payload.slot = editForm.slot;
      if (editForm.address_id !== (orig.address_id || "")) payload.address_id = editForm.address_id;
      if (editForm.instructions !== (orig.instructions || "")) payload.instructions = editForm.instructions;
      if (isPaidOnline) { delete payload.product_id; delete payload.variant_id; delete payload.quantity; }
      if (Object.keys(payload).length === 0) {
        toast("No changes to save");
        return;
      }
      await api.patch(`/api/admin/orders/${editOrder.id}`, payload);
      toast.success("Order updated");
      setEditOrder(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setEditBusy(false);
    }
  };

  // `orders` arrives from the backend already filtered (status/search/route/
  // city), sorted, and paginated — grouping just re-buckets the CURRENT
  // page's rows by customer, same as any other paginated-table grouping.
  const groupedByCustomer = useMemo(() => {
    if (!groupByCustomer) return null;
    const map = new Map();
    orders.forEach((o) => {
      const k = o.customer_id || "_unknown";
      if (!map.has(k)) map.set(k, {
        customer_id: k,
        customer_name: o.customer_name || "—",
        customer_phone: o.customer_phone || "",
        address: o.address || {},
        orders: [], qty: 0, amount: 0, statuses: {},
      });
      const g = map.get(k);
      g.orders.push(o);
      g.qty += o.quantity || 0;
      g.amount += o.amount || 0;
      g.statuses[o.status] = (g.statuses[o.status] || 0) + 1;
    });
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }, [orders, groupByCustomer]);

  const toggleSort = (key) => {
    setSortBy((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };
  const SortHeader = ({ sortKey, children, align = "left" }) => (
    <th className={`text-${align} px-5 py-3 select-none cursor-pointer`} onClick={() => toggleSort(sortKey)}
        data-testid={`sort-${sortKey}`}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy.key === sortKey ? (
          sortBy.dir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>
        ) : null}
      </span>
    </th>
  );

  return (
    <div className="space-y-6 page-enter" data-testid="admin-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Operations</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Orders ({counts.total})</h1>
          <p className="text-sm text-neutral-600 mt-1">
            <Zap className="w-3.5 h-3.5 inline -mt-0.5"/> {counts.one_time} one-time ·{" "}
            <Repeat className="w-3.5 h-3.5 inline -mt-0.5"/> {counts.subscription} subscription ·{" "}
            <b>{formatINR(counts.revenue)}</b> revenue (delivered)
          </p>
        </div>
      </div>

      {/* Date presets */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mr-1">Date</span>
        {DATE_PRESETS.map((d) => (
          <button key={d.v} onClick={() => setDatePreset(d.v)}
                  className={`h-9 px-3 rounded-full text-xs font-bold ${datePreset === d.v ? "bg-brand-ink text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                  data-testid={`date-preset-${d.v}`}>
            {d.label}
          </button>
        ))}
        {datePreset === "custom" && (
          <div className="flex gap-2 items-center" data-testid="custom-date-range">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                   className="h-9 px-3 rounded-full border border-neutral-200 text-xs"
                   data-testid="custom-date-from"/>
            <span className="text-xs">to</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                   className="h-9 px-3 rounded-full border border-neutral-200 text-xs"
                   data-testid="custom-date-to"/>
          </div>
        )}
      </div>

      {/* Delivery slot — the slot saved on each order. Server-side, so it
          narrows every page and the totals, and combines with every other
          filter. */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="orders-slot-filter">
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mr-1">Slot</span>
        {slotOptions(tenant?.delivery_windows, slotCounts).map((o) => (
          <button key={o.value || "all"} onClick={() => setSlotFilter(o.value)}
                  aria-pressed={slotFilter === o.value}
                  className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${slotFilter === o.value ? "bg-brand-ink text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                  data-testid={`orders-slot-${o.value || "all"}`}>
            {o.label}
            <span className={`text-[10px] font-mono ${slotFilter === o.value ? "text-white/70" : "text-neutral-400"}`}>{o.count}</span>
          </button>
        ))}
      </div>

      {/* Type + status + search + group toggle */}
      <div className="flex flex-wrap gap-2 items-center">
        {placedCount > 0 && (
          <button onClick={() => { setType("one_time"); setStatus("placed"); }}
                  className={`h-10 px-4 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${status === "placed" && type === "one_time" ? "bg-brand-clay text-white" : "bg-brand-clay/10 text-brand-clay border border-brand-clay/30"}`}
                  data-testid="filter-placed-pending">
            <ClipboardCheck className="w-3.5 h-3.5"/> Awaiting confirmation · {placedCount}
          </button>
        )}
        {stuckCount > 0 && (
          <button onClick={() => { setStuckOnly((v) => !v); setType(""); setStatus(""); }}
                  className={`h-10 px-4 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${stuckOnly ? "bg-brand-clay text-white" : "bg-brand-clay/10 text-brand-clay border border-brand-clay/30"}`}
                  title="Recurring deliveries with no rider assigned — assign a rider to their route to release them"
                  data-testid="filter-stuck-subscriptions">
            <AlertTriangle className="w-3.5 h-3.5"/> Unassigned recurring · {stuckCount}
          </button>
        )}
        {TYPE_FILTERS.map((f) => (
          <button key={f.v} onClick={() => setType(f.v)}
                  className={`h-10 px-4 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${type === f.v ? "bg-brand-sky text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                  data-testid={`filter-${f.v || "all"}`}>
            <f.Icon className="w-3.5 h-3.5"/> {f.label}
          </button>
        ))}
        <div className="w-px bg-neutral-200 mx-1 hidden sm:block"/>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="h-10 px-4 rounded-full text-sm font-bold border border-neutral-200 bg-white"
                data-testid="filter-status">
          <option value="">All statuses</option>
          {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search customer or product…"
               className="flex-1 min-w-[200px] h-10 px-4 rounded-full text-sm border border-neutral-200 bg-white"
               data-testid="search-input"/>
        <button onClick={() => setGroupByCustomer((g) => !g)}
                className={`h-10 px-4 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${groupByCustomer ? "bg-brand-ink text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                data-testid="group-by-customer-toggle">
          {groupByCustomer ? <Users className="w-3.5 h-3.5"/> : <ListIcon className="w-3.5 h-3.5"/>}
          {groupByCustomer ? "Grouped by customer" : "Group by customer"}
        </button>
      </div>

      {/* Route + City filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mr-1">More</span>
        <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white max-w-full"
                data-testid="orders-route-filter">
          <option value="__all__">All routes</option>
          <option value="__unassigned__">Unassigned</option>
          {filterableRoutes(routes).map((r) => <option key={r.id} value={r.id}>{routeOptionLabel(r)}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white max-w-full"
                data-testid="orders-city-filter">
          <option value="__all__">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {apartments.length > 0 && (
          <select value={apartmentFilter} onChange={(e) => setApartmentFilter(e.target.value)}
                  className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white max-w-full"
                  data-testid="orders-apartment-filter">
            <option value="__all__">All apartments</option>
            {/* Covers manual addresses AND every order placed before this
                feature existed — those rows carry no apartment at all. */}
            <option value="__manual__">Other / manual address</option>
            {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        {(routeFilter !== "__all__" || cityFilter !== "__all__" || apartmentFilter !== "__all__" || slotFilter !== ALL_SLOTS) && (
          <button onClick={() => { setRouteFilter("__all__"); setCityFilter("__all__"); setApartmentFilter("__all__"); setSlotFilter(ALL_SLOTS); }}
                  className="h-10 px-3 rounded-full text-xs font-bold bg-brand-clay/10 text-brand-clay inline-flex items-center gap-1"
                  data-testid="orders-clear-filters">
            <X className="w-3 h-3"/> Clear
          </button>
        )}
      </div>

      {loading && <LoadingState />}

      {!loading && loadError && (
        <EmptyState icon={AlertTriangle} title="Couldn't load orders" description={loadError}
          action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>} />
      )}

      {/* Table */}
      {!loading && !loadError && <section className={`bg-white rounded-3xl border border-neutral-200 overflow-hidden ${pageLoading ? "opacity-60" : ""}`}>
        {/* Mobile card list — same data, vertical stack. */}
        {!groupByCustomer && (
          <ul className="md:hidden divide-y divide-neutral-100" data-testid="orders-mobile-list">
            {orders.length === 0 && (
              <li><EmptyState icon={ShoppingCart} size="sm" title="No orders match" /></li>
            )}
            {orders.map((o) => {
              const s = STATUSES[o.status] || STATUSES.pending;
              const { cc, local } = splitPhone(o.customer_phone || "");
              return (
                <li key={o.id} className="p-4" data-testid={`order-mobile-${o.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-base truncate">{o.customer_name || "—"}</span>
                        {o.order_type === "one_time" ? (
                          <span className="px-2 py-0.5 rounded-full bg-brand-sand/15 text-brand-sand text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1" data-testid={`type-one-time-${o.id}`}>
                            <Zap className="w-3 h-3"/> One-time
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-brand-sky/10 text-brand-sky text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1">
                            <Repeat className="w-3 h-3"/> Sub
                          </span>
                        )}
                      </div>
                      {local && (
                        <div className="text-xs text-neutral-500 font-mono mt-0.5 inline-flex items-center gap-1">
                          <Phone className="w-3 h-3"/> {cc} {local}
                        </div>
                      )}
                      <CustomerUnit address={o.address} requireFlat={requireFlat} className="mt-0.5" testId={`order-unit-${o.id}`}/>
                      <div className="text-sm text-neutral-700 mt-1">
                        {o.product_name} <span className="text-neutral-400">·</span> <SlotBadge slot={o.slot} oid={o.id}/> <span className="text-neutral-400">·</span> <span className="font-mono font-bold">×{o.quantity}</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        <span className="font-mono">{formatDate(o.delivery_date)}</span>
                        {o.route_name && <span className="ml-1 text-brand-leaf font-bold uppercase">· {o.route_name}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-bold">{formatINR(o.amount)}</div>
                      <div className="mt-1"><PaymentBadge method={o.payment_method} oid={o.id} /></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1 ${s.color}`}>
                      <s.Icon className="w-3 h-3"/> {s.label}
                    </span>
                    <RemarksBadge order={o} />
                    <FlowBadge order={o} />
                    <div className="ml-auto flex flex-wrap gap-1.5 items-center">
                      {!["delivered", "failed", "cancelled", "skipped"].includes(o.status) && (
                        <RiderPicker
                          order={o} riders={riders} busy={assignBusy === o.id}
                          onAssign={(rid) => assignRider(o.id, rid)}
                        />
                      )}
                      <button onClick={() => openDetails(o)}
                              className="h-8 px-3 rounded-full bg-neutral-100 text-neutral-600 text-xs font-bold inline-flex items-center gap-1"
                              data-testid={`view-${o.id}`}>
                        <Eye className="w-3.5 h-3.5"/> View
                      </button>
                      {orderEditEnabled && o.order_type === "one_time" && ["placed", "confirmed"].includes(o.status) && (
                        <button onClick={() => openEdit(o)}
                                className="h-8 px-3 rounded-full bg-brand-sky/10 text-brand-sky text-xs font-bold inline-flex items-center gap-1"
                                data-testid={`edit-${o.id}`}>
                          <Pencil className="w-3.5 h-3.5"/> Edit
                        </button>
                      )}
                      {o.order_type === "one_time" && o.status === "placed" && (
                        <button onClick={() => confirmOrder(o.id)}
                                className="h-8 px-3 rounded-full bg-brand-leaf text-white text-xs font-bold inline-flex items-center gap-1"
                                data-testid={`confirm-${o.id}`}>
                          <Check className="w-3.5 h-3.5"/> Confirm
                        </button>
                      )}
                      {!["delivered", "failed", "cancelled", "skipped"].includes(o.status) && (
                        <button onClick={() => cancelOrder(o.id)}
                                className="h-8 px-3 rounded-full bg-brand-clay/10 text-brand-clay text-xs font-bold inline-flex items-center gap-1"
                                data-testid={`cancel-${o.id}`}>
                          <X className="w-3.5 h-3.5"/> Cancel
                        </button>
                      )}
                      {o.status === "delivered" && (
                        <button onClick={() => cancelOrder(o.id)}
                                className="h-8 px-3 rounded-full bg-brand-clay/10 text-brand-clay text-xs font-bold inline-flex items-center gap-1"
                                data-testid={`refund-${o.id}`}>
                          <X className="w-3.5 h-3.5"/> Refund
                        </button>
                      )}
                      {refundDue(o) && (
                        <button onClick={() => retryRefund(o.id)}
                                className="h-8 px-3 rounded-full bg-red-100 text-red-700 text-xs font-bold inline-flex items-center gap-1"
                                data-testid={`retry-refund-${o.id}`}
                                title={o.payment_status === "refund_failed" ? "The refund failed at the gateway — try again" : "Paid online and cancelled, not yet refunded"}>
                          <X className="w-3.5 h-3.5"/> Retry refund
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className={`${groupByCustomer ? "" : "hidden md:block"} overflow-x-auto`}>
          {!groupByCustomer ? (
            <table className="w-full min-w-[900px]">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
                <tr>
                  <SortHeader sortKey="delivery_date">Date · Slot</SortHeader>
                  <SortHeader sortKey="order_type">Type</SortHeader>
                  <SortHeader sortKey="customer_name">Customer</SortHeader>
                  <SortHeader sortKey="route_name">Route</SortHeader>
                  <SortHeader sortKey="product_name">Product</SortHeader>
                  <SortHeader sortKey="quantity" align="right">Qty</SortHeader>
                  <SortHeader sortKey="amount" align="right">Amount</SortHeader>
                  <SortHeader sortKey="payment_method">Payment</SortHeader>
                  <SortHeader sortKey="status">Status</SortHeader>
                  <th className="px-5 py-3 text-left font-bold uppercase tracking-wider">Flow</th>
                  <th className="px-5 py-3 text-left font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {orders.map((o) => {
                  const s = STATUSES[o.status] || STATUSES.pending;
                  const { cc, local } = splitPhone(o.customer_phone || "");
                  return (
                    <tr key={o.id} className="hover:bg-neutral-50" data-testid={`order-row-${o.id}`}>
                      <td className="px-5 py-3 text-sm">
                        <div className="font-mono">{formatDate(o.delivery_date)}</div>
                        <div className="mt-1"><SlotBadge slot={o.slot} oid={o.id}/></div>
                      </td>
                      <td className="px-5 py-3">
                        {o.order_type === "one_time" ? (
                          <span className="px-2 py-0.5 rounded-full bg-brand-sand/15 text-brand-sand text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1" data-testid={`type-one-time-${o.id}`}>
                            <Zap className="w-3 h-3"/> One-time
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-brand-sky/10 text-brand-sky text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1">
                            <Repeat className="w-3 h-3"/> Sub
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm">
                        <div className="font-semibold">{o.customer_name || "—"}</div>
                        {local && (
                          <div className="text-xs text-neutral-500 inline-flex items-center gap-1 font-mono">
                            <Phone className="w-3 h-3"/> {cc} {local}
                          </div>
                        )}
                        <CustomerUnit address={o.address} requireFlat={requireFlat} className="max-w-[220px]" testId={`order-unit-row-${o.id}`}/>
                      </td>
                      <td className="px-5 py-3 text-sm">
                        {o.route_name ? (
                          <span className="px-2 py-0.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold uppercase tracking-wider" data-testid={`route-pill-${o.id}`}>{o.route_name}</span>
                        ) : (
                          <span className="text-neutral-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm">{o.product_name}</td>
                      <td className="px-5 py-3 text-right font-mono font-bold text-sm">{o.quantity}</td>
                      <td className="px-5 py-3 text-right font-mono font-bold text-sm">{formatINR(o.amount)}</td>
                      <td className="px-5 py-3"><PaymentBadge method={o.payment_method} oid={o.id} /></td>
                      <td className="px-5 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1 ${s.color}`}>
                          <s.Icon className="w-3 h-3"/> {s.label}
                        </span>
                        {o.notes && <span className="ml-1.5"><RemarksBadge order={o} /></span>}
                      </td>
                      <td className="px-5 py-3">
                        <FlowBadge order={o} />
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {!["delivered", "failed", "cancelled", "skipped"].includes(o.status) && (
                            <RiderPicker
                              order={o} riders={riders} busy={assignBusy === o.id}
                              onAssign={(rid) => assignRider(o.id, rid)}
                            />
                          )}
                          <button onClick={() => openDetails(o)}
                                  className="h-8 px-3 rounded-full bg-neutral-100 text-neutral-600 text-xs font-bold inline-flex items-center gap-1"
                                  data-testid={`view-${o.id}`}>
                            <Eye className="w-3.5 h-3.5"/> View
                          </button>
                          {orderEditEnabled && o.order_type === "one_time" && ["placed", "confirmed"].includes(o.status) && (
                            <button onClick={() => openEdit(o)}
                                    className="h-8 px-3 rounded-full bg-brand-sky/10 text-brand-sky text-xs font-bold inline-flex items-center gap-1"
                                    data-testid={`edit-${o.id}`}>
                              <Pencil className="w-3.5 h-3.5"/> Edit
                            </button>
                          )}
                          {o.order_type === "one_time" && o.status === "placed" && (
                            <button onClick={() => confirmOrder(o.id)}
                                    className="h-8 px-3 rounded-full bg-brand-leaf text-white text-xs font-bold inline-flex items-center gap-1"
                                    data-testid={`confirm-${o.id}`}>
                              <Check className="w-3.5 h-3.5"/> Confirm
                            </button>
                          )}
                          {!["delivered", "failed", "cancelled", "skipped"].includes(o.status) && (
                            <button onClick={() => cancelOrder(o.id)}
                                    className="h-8 px-3 rounded-full bg-brand-clay/10 text-brand-clay text-xs font-bold inline-flex items-center gap-1"
                                    data-testid={`cancel-${o.id}`}>
                              <X className="w-3.5 h-3.5"/> Cancel
                            </button>
                          )}
                          {o.status === "delivered" && (
                            <button onClick={() => cancelOrder(o.id)}
                                    className="h-8 px-3 rounded-full bg-brand-clay/10 text-brand-clay text-xs font-bold inline-flex items-center gap-1"
                                    data-testid={`refund-${o.id}`}
                                    title="Cancel delivered order and refund what was paid">
                              <X className="w-3.5 h-3.5"/> Refund
                            </button>
                          )}
                          {refundDue(o) && (
                            <button onClick={() => retryRefund(o.id)}
                                    className="h-8 px-3 rounded-full bg-red-100 text-red-700 text-xs font-bold inline-flex items-center gap-1"
                                    data-testid={`retry-refund-${o.id}`}
                                    title={o.payment_status === "refund_failed" ? "The refund failed at the gateway — try again" : "Paid online and cancelled, not yet refunded"}>
                              <X className="w-3.5 h-3.5"/> Retry refund
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {orders.length === 0 && (
                  <tr><td colSpan="11"><EmptyState icon={ShoppingCart} size="sm" title="No orders match" /></td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[700px]">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
                <tr>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-right px-5 py-3">Orders</th>
                  <th className="text-right px-5 py-3">Total qty</th>
                  <th className="text-right px-5 py-3">Total amount</th>
                  <th className="text-left px-5 py-3">Breakdown</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(groupedByCustomer || []).map((g) => (
                  <tr key={g.customer_id} className="hover:bg-neutral-50 align-top" data-testid={`group-row-${g.customer_id}`}>
                    <td className="px-5 py-4">
                      <div className="font-semibold">{g.customer_name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{g.customer_phone}</div>
                      <CustomerUnit address={g.address} requireFlat={requireFlat} testId={`group-unit-${g.customer_id}`}/>
                    </td>
                    <td className="px-5 py-4 text-right font-mono font-bold">{g.orders.length}</td>
                    <td className="px-5 py-4 text-right font-mono font-bold">{g.qty}</td>
                    <td className="px-5 py-4 text-right font-mono font-bold">{formatINR(g.amount)}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {Object.entries(g.statuses).map(([s, n]) => {
                          const cfg = STATUSES[s] || STATUSES.pending;
                          return (
                            <span key={s} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>
                              {cfg.label}: {n}
                            </span>
                          );
                        })}
                      </div>
                      <div className="mt-2 text-xs text-neutral-500 line-clamp-2">
                        {g.orders.slice(0, 3).map((o) => `${o.product_name}×${o.quantity} (${slotLabel(o.slot)})`).join(" · ")}
                        {g.orders.length > 3 && ` · +${g.orders.length - 3} more`}
                      </div>
                    </td>
                  </tr>
                ))}
                {(groupedByCustomer || []).length === 0 && (
                  <tr><td colSpan="5"><EmptyState icon={ShoppingCart} size="sm" title="No orders match" /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>}

      {!loading && !loadError && (
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
                    onPageChange={goToPage} loading={pageLoading} testId="orders-pagination" />
      )}

      {/* Order edit dialog */}
      <Dialog open={!!editOrder} onOpenChange={(o) => !o && setEditOrder(null)}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="order-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit order</DialogTitle></DialogHeader>
          {editOrder && (
            <div className="space-y-4">
              <div className="bg-brand-cream rounded-2xl p-4">
                <div className="font-bold">{editOrder.customer_name}</div>
                <div className="text-sm text-neutral-600">{editOrder.product_name} × {editOrder.quantity} · {formatINR(editOrder.amount)}</div>
              </div>

              {isPaidOnline && (
                <div className="rounded-2xl bg-brand-sand/15 border border-brand-sand/40 p-3 text-xs text-neutral-700">
                  This order was already paid online — product/quantity can&rsquo;t be changed here (no partial-refund flow exists).
                  Cancel the order (auto-refunds the wallet) and place a new one instead if the items need to change.
                </div>
              )}

              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${isPaidOnline ? "opacity-50 pointer-events-none" : ""}`}>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Product</label>
                  <select value={editForm.product_id}
                          onChange={(e) => setEditForm((f) => ({ ...f, product_id: e.target.value, variant_id: "" }))}
                          className="w-full h-11 rounded-2xl border border-neutral-200 px-3 mt-1 text-sm bg-white"
                          data-testid="edit-product-select" disabled={isPaidOnline}>
                    {editProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Variant</label>
                  <select value={editForm.variant_id || ""}
                          onChange={(e) => setEditForm((f) => ({ ...f, variant_id: e.target.value }))}
                          className="w-full h-11 rounded-2xl border border-neutral-200 px-3 mt-1 text-sm bg-white"
                          data-testid="edit-variant-select" disabled={isPaidOnline}>
                    {(() => {
                      const p = editProducts.find((x) => x.id === editForm.product_id);
                      const variants = p?.variants?.length ? p.variants : [{ id: "", label: p?.pack_size || "Default" }];
                      return variants.map((v) => <option key={v.id || "default"} value={v.id || ""}>{v.label}</option>);
                    })()}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Quantity</label>
                  <Input type="number" min="1" value={editForm.quantity}
                         onChange={(e) => setEditForm((f) => ({ ...f, quantity: e.target.value }))}
                         className="h-11 rounded-2xl mt-1" data-testid="edit-quantity-input" disabled={isPaidOnline}/>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Delivery date</label>
                  <Input type="date" value={editForm.delivery_date}
                         onChange={(e) => setEditForm((f) => ({ ...f, delivery_date: e.target.value }))}
                         className="h-11 rounded-2xl mt-1" data-testid="edit-date-input"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Slot</label>
                  <select value={editForm.slot}
                          onChange={(e) => setEditForm((f) => ({ ...f, slot: e.target.value }))}
                          className="w-full h-11 rounded-2xl border border-neutral-200 px-3 mt-1 text-sm bg-white capitalize"
                          data-testid="edit-slot-select">
                    {["morning", "evening"].includes(editForm.slot) ? null : <option value={editForm.slot}>{editForm.slot}</option>}
                    <option value="morning">Morning</option>
                    <option value="evening">Evening</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Address</label>
                {editAddressError ? (
                  <>
                    <div className="w-full h-11 rounded-2xl border border-neutral-200 px-3 mt-1 text-sm bg-neutral-50 text-neutral-500 flex items-center truncate"
                         data-testid="edit-address-readonly">
                      {editOrder?.address ? `${editOrder.address.label || "Address"} — ${editOrder.address.line1}${editOrder.address.city ? `, ${editOrder.address.city}` : ""}` : "—"}
                    </div>
                    <div className="text-[11px] text-brand-clay mt-1" data-testid="edit-address-error">
                      Couldn&rsquo;t load this customer&rsquo;s saved addresses ({editAddressError}) — address can&rsquo;t be changed here, but the rest of the order is still editable.
                    </div>
                  </>
                ) : (
                  <select value={editForm.address_id}
                          onChange={(e) => setEditForm((f) => ({ ...f, address_id: e.target.value }))}
                          className="w-full h-11 rounded-2xl border border-neutral-200 px-3 mt-1 text-sm bg-white"
                          data-testid="edit-address-select">
                    {editAddresses.map((a) => (
                      <option key={a.id} value={a.id}>{a.label} — {a.line1}{a.city ? `, ${a.city}` : ""}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Instructions</label>
                <Input value={editForm.instructions}
                       onChange={(e) => setEditForm((f) => ({ ...f, instructions: e.target.value }))}
                       className="h-11 rounded-2xl mt-1" data-testid="edit-instructions-input"/>
              </div>

              <DialogFooter>
                <Button onClick={saveEdit} disabled={editBusy || editLoading}
                        className="w-full h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="save-edit-btn">
                  {editBusy ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Order details dialog — read-only, opens for any order regardless of
          status. The reason a rider (or admin) gave for a cancelled/failed/
          skipped delivery is shown here as a clearly-labeled, always-visible
          block — not just the hover-only icon in the list. */}
      <Dialog open={!!viewOrder} onOpenChange={(o) => !o && setViewOrder(null)}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="order-details-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Order details</DialogTitle></DialogHeader>
          {viewOrder && (() => {
            const s = STATUSES[viewOrder.status] || STATUSES.pending;
            const { cc, local } = splitPhone(viewOrder.customer_phone || "");
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1 ${s.color}`}>
                    <s.Icon className="w-3 h-3"/> {s.label}
                  </span>
                  {viewOrder.order_type === "one_time" ? (
                    <span className="px-2 py-0.5 rounded-full bg-brand-sand/15 text-brand-sand text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1">
                      <Zap className="w-3 h-3"/> One-time
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-brand-sky/10 text-brand-sky text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1">
                      <Repeat className="w-3 h-3"/> Subscription
                    </span>
                  )}
                </div>

                {/* The reason — front and center, above the rest of the
                    order's details, so it can never be missed. */}
                {viewOrder.notes && (
                  <div className="rounded-2xl bg-brand-clay/10 border border-brand-clay/30 p-4" data-testid="order-details-reason">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-brand-clay flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5"/> {REASON_LABELS[viewOrder.status] || "Remarks"}
                    </div>
                    <div className="text-sm text-neutral-800 mt-1.5 whitespace-pre-wrap">{viewOrder.notes}</div>
                  </div>
                )}

                <div className="bg-brand-cream rounded-2xl p-4 space-y-1">
                  <div className="font-bold">{viewOrder.customer_name || "—"}</div>
                  <CustomerUnit address={viewOrder.address} requireFlat={requireFlat} testId="order-details-unit"/>
                  {local && (
                    <div className="text-xs text-neutral-500 inline-flex items-center gap-1 font-mono">
                      <Phone className="w-3 h-3"/> {cc} {local}
                    </div>
                  )}
                  <div className="text-sm text-neutral-700">
                    {viewOrder.product_name} × {viewOrder.quantity} · <span className="font-mono font-bold">{formatINR(viewOrder.amount)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Delivery date</div>
                    <div className="font-mono mt-0.5">{formatDate(viewOrder.delivery_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Slot</div>
                    <div className="capitalize mt-0.5">{viewOrder.slot || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Route</div>
                    <div className="mt-0.5">{viewOrder.route_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Rider</div>
                    <div className="mt-0.5">{viewOrder.rider_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Payment</div>
                    <div className="mt-0.5"><PaymentBadge method={viewOrder.payment_method} oid={viewOrder.id}/></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">City</div>
                    <div className="mt-0.5">{viewOrder.customer_city || "—"}</div>
                  </div>
                </div>

                {viewOrder.instructions && (
                  <div>
                    {/* Say WHAT kind of note this is: a subscription row's
                        stamped instruction shows on every day of that
                        subscription — it is not a note for this date. */}
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500" data-testid="instructions-label">
                      {viewOrder.order_type === "one_time" ? "Delivery instructions"
                        : viewOrder.instructions_source === "day" ? "Note for this delivery"
                        : "Subscription instruction — shows on every delivery of this subscription"}
                    </div>
                    <div className="text-sm mt-0.5">{viewOrder.instructions}</div>
                  </div>
                )}
                {viewOrder.order_type !== "one_time" && ["placed", "pending", "confirmed"].includes(viewOrder.status) && (
                  <div data-testid="day-note-editor">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Note for this delivery only</div>
                    <div className="flex gap-2 mt-1">
                      <Input value={dayNote} onChange={(e) => setDayNote(e.target.value)}
                             placeholder={`Only ${formatDate(viewOrder.delivery_date)} — e.g. “2 packets extra today”`}
                             className="h-10 rounded-2xl flex-1" data-testid="day-note-input"/>
                      <Button onClick={saveDayNote} disabled={dayNoteBusy || dayNote === (viewOrder.instructions_source === "day" ? (viewOrder.instructions || "") : "")}
                              className="h-10 rounded-2xl bg-brand-ink hover:bg-black font-bold px-4" data-testid="day-note-save">Save</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Auto-flow indicator: green "Auto" pill when a route+rider have been assigned
 * to this delivery (so it will reach the rider automatically), amber "Manual"
 * pill when the order is still waiting on a route assignment. Terminal orders
 * (delivered/cancelled/failed/skipped) just show a neutral dash.
 */
/** Rider-entered remarks (delivery-failure/skip/cancel reason) — a small
 * icon with a native tooltip next to the status badge, shown only when a
 * rider actually left a note. Data (`Delivery.notes`) already flows through
 * the order-list API; this was previously never rendered. */
function RemarksBadge({ order }) {
  if (!order.notes) return null;
  return (
    <span title={order.notes} className="text-neutral-400 hover:text-neutral-600 cursor-help" data-testid={`remarks-${order.id}`}>
      <MessageSquare className="w-3.5 h-3.5"/>
    </span>
  );
}

function FlowBadge({ order }) {
  const terminal = ["delivered", "cancelled", "failed", "skipped"].includes(order.status);
  if (terminal) {
    return <span className="text-neutral-400 text-xs" data-testid={`flow-na-${order.id}`}>—</span>;
  }
  const assigned = !!order.delivery_boy_id;
  if (assigned) {
    return (
      <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1 bg-brand-leaf/10 text-brand-leaf"
            title="Route + rider assigned — order flows automatically"
            data-testid={`flow-auto-${order.id}`}>
        <UserCheck className="w-3 h-3"/> Auto
      </span>
    );
  }
  return (
    <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1 bg-brand-sand/15 text-brand-sand"
          title="No rider assigned — manual handling required"
          data-testid={`flow-manual-${order.id}`}>
      <UserPlus className="w-3 h-3"/> Manual
    </span>
  );
}

/** Payment-method pill: wallet (auto-debit), cod (cash on delivery), online. */
function PaymentBadge({ method, oid }) {
  const m = (method || "wallet").toLowerCase();
  const map = {
    wallet: { Icon: Wallet, label: "Wallet", cls: "bg-brand-sky/10 text-brand-sky" },
    cod: { Icon: Banknote, label: "Cash on Delivery", cls: "bg-brand-sand/15 text-brand-sand" },
    online: { Icon: CreditCard, label: "Paid Online", cls: "bg-brand-leaf/10 text-brand-leaf" },
  };
  const cfg = map[m] || map.wallet;
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1 ${cfg.cls}`}
          data-testid={`payment-${m}-${oid}`}>
      <cfg.Icon className="w-3 h-3"/> {cfg.label}
    </span>
  );
}


/** Inline rider picker — assigns / changes / clears the delivery boy on a
 * single order. Renders as a compact select whose value = current
 * ``delivery_boy_id``. Empty option ("— No rider —") clears the assignment
 * and demotes ``confirmed`` orders back to ``placed`` so admins spot them
 * in the manual-handling filter. Terminal statuses are hidden from this
 * control at the call site. */
function RiderPicker({ order, riders, busy, onAssign }) {
  const current = order.delivery_boy_id || "";
  return (
    <label className="inline-flex items-center gap-1 text-[11px]" data-testid={`rider-picker-wrap-${order.id}`}>
      <UserCog className="w-3.5 h-3.5 text-neutral-500 shrink-0"/>
      <select
        value={current}
        disabled={busy}
        onChange={(e) => onAssign(e.target.value)}
        data-testid={`rider-picker-${order.id}`}
        className="h-8 rounded-full border border-neutral-300 bg-white pl-2 pr-6 text-[11px] font-bold max-w-[160px] focus:outline-none focus:ring-2 focus:ring-brand-ink disabled:opacity-50"
        title={order.rider_name ? `Currently: ${order.rider_name}` : "No rider assigned"}
      >
        <option value="">— No rider —</option>
        {riders.map((r) => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </select>
    </label>
  );
}
